import { CheckCircle, CloseCircle, Celebrate } from '../../../mads/icons';
import { useI18n } from '../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens/react-native';
import React from 'react';

export enum OutreachStatus {
    INITIATED = 'INITIATED',
    INTERESTED = 'INTERESTED',
    DECLINED = 'DECLINED',
}

export enum OutreachType {
    FAST_APPLY = 'FAST_APPLY',
    FULL_APPLY = 'FULL_APPLY',
}

export interface OutreachInfo {
    outreachStatus: OutreachStatus;
    outreachType: OutreachType;
    employerName?: string;
}

export interface EmployerOutreachUIConfig {
    textContent?: {
        header?: string;
        icon?: React.ReactNode;
        subheader?: string;
    };
    acceptButton?: {
        text: string;
        disabled: boolean;
    };
    declineButton?: {
        text: string;
        disabled: boolean;
    };
}

const confirmationIcon = <CheckCircle color={colors.foregroundStatusSuccess} size="sm" />;
const declinedIcon = <CloseCircle color={colors.foregroundStatusCritical} size="sm" />;
const createdIcon = <Celebrate color={colors.foregroundActionPrimary} size="sm" />;

const createOutreachUIConfig = (
    options: {
        header?: string;
        subheader?: string;
        icon?: React.ReactNode;
        acceptButton?: { text: string; disabled?: boolean };
        declineButton?: { text: string; disabled?: boolean };
    } = {},
): EmployerOutreachUIConfig => {
    const config: EmployerOutreachUIConfig = {};

    if (options.header || options.subheader || options.icon) {
        config.textContent = {
            header: options.header,
            subheader: options.subheader,
            icon: options.icon,
        };
    }

    if (options.acceptButton) {
        config.acceptButton = {
            text: options.acceptButton.text,
            disabled: options.acceptButton.disabled ?? false,
        };
    }

    if (options.declineButton) {
        config.declineButton = {
            text: options.declineButton.text,
            disabled: options.declineButton.disabled ?? false,
        };
    }

    return config;
};

export const useEmployerOutreachUIConfig = (
    info: OutreachInfo | null,
): EmployerOutreachUIConfig => {
    const { gettext } = useI18n();

    if (!info) return createOutreachUIConfig();

    if (info.outreachStatus === OutreachStatus.INTERESTED) {
        if (info.outreachType === OutreachType.FAST_APPLY) {
            return createOutreachUIConfig({
                header: gettext('Interest confirmed'),
                subheader: gettext('Your response has been sent to the employer.'),
                icon: confirmationIcon,
            });
        }
    } else if (info.outreachStatus === OutreachStatus.INITIATED) {
        if (info.outreachType === OutreachType.FAST_APPLY) {
            return createOutreachUIConfig({
                header: gettext('Skip the application process by confirming your interest'),
                icon: createdIcon,
                declineButton: { text: gettext('Decline') },
                acceptButton: { text: gettext('Confirm my interest') },
            });
        } else if (info.outreachType === OutreachType.FULL_APPLY) {
            // Full apply uses current Indeed apply button so no accept button config needed
            const employerName = info.employerName || 'The employer';
            return createOutreachUIConfig({
                header: gettext('{0} has invited you to apply', employerName),
                icon: createdIcon,
                declineButton: { text: gettext('Decline') },
            });
        }
    } else if (info.outreachStatus === OutreachStatus.DECLINED) {
        return createOutreachUIConfig({
            header: gettext('Opportunity declined'),
            subheader: gettext(
                "You've let the employer know you're not interested in this position at this time.",
            ),
            icon: declinedIcon,
        });
    }

    return createOutreachUIConfig();
};
